import {
  FC, useEffect, useRef, useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { create } from 'zustand';

/**
 * Pasek postępu przejścia między widokami (nad topbarem, jak na wielu stronach).
 *
 * Startuje przy zmianie adresu i trwa, dopóki leniwie ładowany widok jest w drodze:
 * fallback granicy Suspense (`RouteProgressTrigger`) zgłasza „ładuję” przy montażu
 * i „gotowe” przy odmontowaniu. Bez ładowania pasek tylko krótko przemyka.
 */
const useRouteProgressStore = create<{ pending: number; inc:() => void; dec: () => void }>((set) => ({
  pending: 0,
  inc: () => set((s) => ({ pending: s.pending + 1 })),
  dec: () => set((s) => ({ pending: Math.max(0, s.pending - 1) })),
}));

/** Wstaw w `fallback` granicy Suspense: pasek trwa, póki widok się ładuje. */
export const RouteProgressTrigger: FC = () => {
  const { inc, dec } = useRouteProgressStore();
  useEffect(() => {
    inc();
    return () => dec();
  }, [inc, dec]);
  return null;
};

const Track = styled.div<{ $visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  z-index: 2000;
  pointer-events: none;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 200ms ease;
`;

const Bar = styled.div<{ $width: number }>`
  height: 100%;
  width: ${({ $width }) => $width}%;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#f3f4f6' : '#111827')};
  transition: ${({ $width }) => ($width === 0 ? 'none' : 'width 300ms ease-out')};
`;

export const RouteProgress: FC = () => {
  const { pathname } = useLocation();
  const pending = useRouteProgressStore((s) => s.pending);
  const [width, setWidth] = useState(0);
  const [visible, setVisible] = useState(false);
  const timers = useRef<number[]>([]);
  const first = useRef(true);

  const clear = () => { timers.current.forEach((id) => window.clearTimeout(id)); timers.current = []; };
  const later = (fn: () => void, ms: number) => { timers.current.push(window.setTimeout(fn, ms)); };

  const start = () => {
    clear();
    setVisible(true);
    setWidth(8);
    later(() => setWidth(45), 30);
    later(() => setWidth(75), 400);
    later(() => setWidth(88), 1200);
  };

  const finish = () => {
    clear();
    setWidth(100);
    later(() => setVisible(false), 250);
    later(() => setWidth(0), 500);
  };

  // Zmiana adresu: start; jeśli nic się nie ładuje, kończymy po chwili.
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    start();
    later(() => { if (useRouteProgressStore.getState().pending === 0) finish(); }, 250);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Ładowanie widoku: trzymamy pasek; po załadowaniu domykamy.
  useEffect(() => {
    if (pending > 0) {
      if (!visible) start();
    } else if (visible) {
      finish();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  useEffect(() => () => clear(), []);

  return (
    <Track $visible={visible} aria-hidden="true">
      <Bar $width={width} />
    </Track>
  );
};
